import * as Sentry from '@sentry/react';
import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import { queryUtils, useDeleteBenchmarkMutation, useUpdateBenchmarkMutation } from '@vizit/api';
import { SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS } from '@vizit/constants';
import { BenchmarkAction, type BenchmarkEditValues } from '@vizit/organisms';
import * as React from 'react';
import { useBenchmarkCollections } from './use-benchmark-collections';

/** Bulk download of a benchmark's images has no endpoint yet, so the menu shows it disabled. */
const DISABLED_BENCHMARK_ACTIONS = [BenchmarkAction.Download];

/** Refetches the workspace's benchmark lists, its collections' benchmark pickers and any open benchmark page. */
function refreshBenchmarks(queryClient: QueryClient, workspaceId: string) {
  return Promise.all([
    queryUtils.invalidateAllBenchmarkLists(queryClient, workspaceId),
    queryClient.invalidateQueries({
      predicate: ({ queryKey: [name, variables] }) =>
        name === 'GetBenchmark' && (variables as { workspace_id?: string } | undefined)?.workspace_id === workspaceId,
    }),
  ]);
}

interface BenchmarkActionTarget {
  id: string;
  name: string;
  description?: string | null;
  /** Collections scored against the benchmark, as its listing reports it; the delete confirm counts from it. */
  collectionCount?: number;
}

interface UseBenchmarkActionsProps {
  workspaceId?: string;
  /** The benchmarks the menu can act on, looked up by ID */
  benchmarks: BenchmarkActionTarget[];
  /** The page URL of a benchmark, for "Open in new tab" */
  getBenchmarkHref?: (id: string) => string;
}

/**
 * State and handlers behind a benchmark's "⋯" menu: which edit or delete dialog is open, saving an edit (only the
 * fields that changed), deleting, and opening the benchmark in a new tab. Download stays disabled until the API
 * can bundle a benchmark's images.
 */
function useBenchmarkActions({ workspaceId, benchmarks, getBenchmarkHref }: UseBenchmarkActionsProps) {
  const queryClient = useQueryClient();
  const { mutateAsync: updateBenchmark } = useUpdateBenchmarkMutation();
  const { mutateAsync: deleteBenchmark } = useDeleteBenchmarkMutation();

  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [deletingId, setDeletingId] = React.useState<string | null>(null);
  const [editErrorMessage, setEditErrorMessage] = React.useState<string | null>(null);
  const [deleteErrorMessage, setDeleteErrorMessage] = React.useState<string | null>(null);

  const editing = benchmarks.find((benchmark) => benchmark.id === editingId) ?? null;
  const deleting = benchmarks.find((benchmark) => benchmark.id === deletingId) ?? null;

  // The names load for the confirm's list; the count is already on the benchmark, so the acknowledgement is instant.
  const { collections: affectedCollections } = useBenchmarkCollections({ workspaceId, benchmarkId: deletingId });

  const onBenchmarkAction = React.useCallback(
    (id: string, action: BenchmarkAction) => {
      switch (action) {
        case BenchmarkAction.Edit:
          setEditErrorMessage(null);
          setEditingId(id);
          break;
        case BenchmarkAction.Delete:
          setDeleteErrorMessage(null);
          setDeletingId(id);
          break;
        case BenchmarkAction.OpenInNewTab:
          if (getBenchmarkHref) {
            window.open(getBenchmarkHref(id), '_blank', 'noopener,noreferrer');
          }
          break;
        default:
          break;
      }
    },
    [getBenchmarkHref],
  );

  const onSubmitEdit = React.useCallback(
    async (values: BenchmarkEditValues) => {
      if (!workspaceId || !editing) {
        return;
      }
      const changes = {
        ...(values.name !== editing.name ? { name: values.name } : {}),
        ...(values.description !== (editing.description ?? null) ? { description: values.description } : {}),
      };
      try {
        await updateBenchmark({ workspace_id: workspaceId, benchmark_id: editing.id, ...changes });
        await refreshBenchmarks(queryClient, workspaceId);
        setEditingId(null);
      } catch (error) {
        Sentry.captureException(error, {
          tags: {
            feature: SENTRY_FEATURE_TAGS.Benchmark,
            stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Benchmark].benchmarkUpdate,
          },
          extra: { workspace_id: workspaceId, benchmark_id: editing.id },
        });
        setEditErrorMessage("Couldn't save the benchmark. Try again.");
        throw error;
      }
    },
    [workspaceId, editing, updateBenchmark, queryClient],
  );

  const onConfirmDelete = React.useCallback(async () => {
    if (!workspaceId || !deleting) {
      return;
    }
    try {
      await deleteBenchmark({ workspace_id: workspaceId, benchmark_id: deleting.id });
      await refreshBenchmarks(queryClient, workspaceId);
      setDeletingId(null);
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          feature: SENTRY_FEATURE_TAGS.Benchmark,
          stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Benchmark].benchmarkDelete,
        },
        extra: { workspace_id: workspaceId, benchmark_id: deleting.id },
      });
      setDeleteErrorMessage("Couldn't delete the benchmark. Try again.");
    }
  }, [workspaceId, deleting, deleteBenchmark, queryClient]);

  return {
    onBenchmarkAction,
    disabledActions: DISABLED_BENCHMARK_ACTIONS,
    editing,
    editErrorMessage,
    onEditOpenChange: (isOpen: boolean) => !isOpen && setEditingId(null),
    onSubmitEdit,
    deleting,
    affectedCollections,
    affectedCollectionCount: deleting?.collectionCount,
    deleteErrorMessage,
    onDeleteOpenChange: (isOpen: boolean) => !isOpen && setDeletingId(null),
    onConfirmDelete,
  };
}

export { useBenchmarkActions };
